<template>
  <div class="queue-task-item" :class="taskStatusClass">
    <div class="task-icon">{{ taskIcon }}</div>
    <div class="task-content">
      <div class="task-info">
        <div class="task-name">{{ taskDisplayName }}</div>
        <div class="task-status">{{ taskStatusText }}</div>
      </div>
      <div class="task-progress" v-if="task.status === 'processing'">
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: `${task.progress}%` }"
          ></div>
        </div>
        <span class="progress-text">{{ task.progress }}%</span>
      </div>
      <div class="task-error" v-if="task.status === 'error' && task.error">
        <span class="error-text">{{ task.error }}</span>
      </div>
    </div>
    <div class="task-actions" v-if="showActions">
      <button 
        v-if="task.status === 'error'" 
        class="action-btn retry-btn"
        @click="$emit('retry', task.id)"
        title="再試行"
      >
        🔄
      </button>
      <button 
        v-if="task.status === 'pending' || task.status === 'processing'" 
        class="action-btn cancel-btn"
        @click="$emit('cancel', task.id)"
        title="キャンセル"
      >
        ✕
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { QUEUE_STATUS, QUEUE_TYPES } from '@/stores/storageStore'

const props = defineProps({
  task: {
    type: Object,
    required: true
  },
  showActions: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['cancel', 'retry'])

// タスク状態のアイコンとスタイル
const TASK_DISPLAY = {
  [QUEUE_STATUS.PENDING]: {
    icon: '⏸️',
    color: '#6c757d',
    text: '待機中'
  },
  [QUEUE_STATUS.PROCESSING]: {
    icon: '⏳',
    color: '#007bff', 
    text: '処理中'
  },
  [QUEUE_STATUS.COMPLETED]: {
    icon: '✅',
    color: '#28a745',
    text: '完了'
  },
  [QUEUE_STATUS.ERROR]: {
    icon: '❌',
    color: '#dc3545',
    text: 'エラー'
  },
  [QUEUE_STATUS.CANCELLED]: {
    icon: '⏹️',
    color: '#6c757d',
    text: 'キャンセル済み'
  }
}

const taskIcon = computed(() => {
  return TASK_DISPLAY[props.task.status]?.icon || '📄'
})

const taskStatusClass = computed(() => {
  return `status-${props.task.status}`
})

const taskStatusText = computed(() => {
  return TASK_DISPLAY[props.task.status]?.text || props.task.status
})

const taskDisplayName = computed(() => {
  const { type, data } = props.task
  
  switch (type) {
    case QUEUE_TYPES.UPLOAD:
      return data.file?.name || 'ファイルアップロード'
    case QUEUE_TYPES.DELETE:
      return `${data.fileName} (削除)`
    case QUEUE_TYPES.RENAME:
      return `${data.oldName} → ${data.newName}`
    default:
      return 'ファイル操作'
  }
})
</script>

<style scoped>
.queue-task-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  transition: background-color 0.2s ease;
  border-left: 3px solid transparent;
}

.queue-task-item:hover {
  background-color: #f8f9fa;
}

.queue-task-item.status-processing {
  border-left-color: #007bff;
}

.queue-task-item.status-completed {
  border-left-color: #28a745;
}

.queue-task-item.status-error {
  border-left-color: #dc3545;
}

.queue-task-item.status-pending {
  border-left-color: #6c757d;
}

.queue-task-item.status-cancelled {
  border-left-color: #6c757d;
  opacity: 0.7;
}

.task-icon {
  font-size: 16px;
  margin-right: 12px;
  flex-shrink: 0;
}

.task-content {
  flex: 1;
  min-width: 0;
}

.task-info {
  margin-bottom: 4px;
}

.task-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.task-status {
  font-size: 11px;
  color: #6c757d;
  font-weight: 500;
}

.task-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.progress-bar {
  flex: 1;
  height: 3px;
  background-color: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff, #0056b3);
  transition: width 0.3s ease;
  border-radius: 2px;
}

.progress-text {
  font-size: 10px;
  color: #6c757d;
  font-weight: 500;
  min-width: 30px;
  text-align: right;
}

.task-error {
  margin-top: 4px;
}

.error-text {
  font-size: 11px;
  color: #dc3545;
  font-style: italic;
}

.task-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.action-btn {
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: all 0.2s ease;
}

.retry-btn:hover {
  background-color: #28a745;
  color: white;
}

.cancel-btn:hover {
  background-color: #dc3545;
  color: white;
}
</style>